package org.teddapp.project.ui.focus

import org.teddapp.project.ui.TeddSpace

import org.teddapp.project.ui.TeddEmptyState

import org.teddapp.project.ui.TeddTagTone

import org.teddapp.project.ui.TeddTag

import org.teddapp.project.ui.TeddTabs

import org.teddapp.project.ui.TeddTab

import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.tedd.db.Pomodoro_item
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import org.teddapp.project.ui.skills.formatMinutes
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.LocalTeddFonts
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.pomodoro.POM_PRESET_DURATIONS

// ──────────────────────────────────────────────────────────────────────
// Composants partagés par les mises en page compacte et étendue.
// Une seule implémentation, un paramètre `compact` quand l'espacement diffère.
// ──────────────────────────────────────────────────────────────────────

/** Anneau du minuteur : piste discrète, arc à l'accent qui respire quand il tourne. */
@Composable
internal fun FocusTimerRing(
    progress: Float,
    active: Boolean,
    modifier: Modifier = Modifier,
    strokeWidth: Dp = 10.dp,
) {
    val track = TeddTokens.SurfaceSunken
    val arc = TeddTokens.LimeStrong
    val transition = rememberInfiniteTransition(label = "ring-pulse")
    val glow by transition.animateFloat(
        initialValue = 0.7f,
        targetValue = 1f,
        animationSpec = infiniteRepeatable(
            animation = tween(durationMillis = 1800, easing = LinearEasing),
            repeatMode = RepeatMode.Reverse,
        ),
        label = "glow",
    )
    Canvas(modifier = modifier.fillMaxSize()) {
        val stroke = strokeWidth.toPx()
        val r = (size.minDimension - stroke) / 2f
        val topLeft = Offset((size.width - r * 2f) / 2f, (size.height - r * 2f) / 2f)
        val arcSize = Size(r * 2f, r * 2f)
        drawArc(
            color = track, startAngle = -90f, sweepAngle = 360f, useCenter = false,
            topLeft = topLeft, size = arcSize, style = Stroke(width = stroke, cap = StrokeCap.Round),
        )
        if (active && progress > 0f) {
            drawArc(
                color = arc, startAngle = -90f, sweepAngle = 360f * progress, useCenter = false,
                topLeft = topLeft, size = arcSize, style = Stroke(width = stroke, cap = StrokeCap.Round),
                alpha = glow,
            )
        }
    }
}

private fun mmss(minutes: Int, seconds: Int): String =
    minutes.toString().padStart(2, '0') + ":" + seconds.toString().padStart(2, '0')

/**
 * Minuteur : grand chiffre, un seul bouton au repos, deux quand il tourne.
 * Étendu : colonne avec anneau de 200 dp. Compact : carte horizontale.
 */
@Composable
internal fun FocusTimerCard(model: FocusModel, expanded: Boolean, modifier: Modifier = Modifier) {
    val mins = if (model.timerActive) model.timerRemainingSec / 60 else model.timerMinutes
    val secs = if (model.timerActive) model.timerRemainingSec % 60 else 0
    val remote = model.remoteTimerDevice
    val label = when {
        model.timerActive && model.timerPaused -> "EN PAUSE"
        remote != null -> "LANCÉ SUR ${remote.uppercase()}"
        model.timerActive -> "FOCUS EN COURS"
        else -> "${model.timerMinutes} MIN CIBLE"
    }
    if (expanded) {
        Column(
            modifier = modifier.fillMaxWidth(),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(TeddSpace.S4),
        ) {
            // L'état se lit en toutes lettres ; le citron vert ne s'allume que quand ça tourne.
            when {
                model.timerActive && model.timerPaused -> TeddTag("En pause", icon = "pause")
                remote != null -> TeddTag("Lancé sur $remote", tone = TeddTagTone.Brand)
                model.timerActive -> TeddTag("En cours", tone = TeddTagTone.Lime)
                else -> TeddTag("Session de ${model.timerMinutes} min", tone = TeddTagTone.Outline)
            }
            Box(modifier = Modifier.size(216.dp), contentAlignment = Alignment.Center) {
                FocusTimerRing(progress = model.timerProgress, active = model.timerActive)
                Text(text = mmss(mins, secs), color = TeddTokens.Ink, style = TeddType.Timer.copy(fontSize = 52.sp, lineHeight = 56.sp))
            }
            TimerButtons(model)
        }
    } else {
        // Sur téléphone, le temps et le bouton se partagent une ligne ; l'indication de
        // compétence, trop longue pour tenir à côté, passe dessous. Sinon la colonne du
        // temps est écrasée et « 25:00 » s'affiche en colonne de chiffres.
        Column(
            modifier = modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 8.dp)
                .clip(RoundedCornerShape(TeddTokens.RLg))
                .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RLg))
                .padding(14.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(14.dp)) {
                Box(modifier = Modifier.size(64.dp), contentAlignment = Alignment.Center) {
                    FocusTimerRing(progress = model.timerProgress, active = model.timerActive, strokeWidth = 6.dp)
                    TeddIcon(name = if (model.timerActive && !model.timerPaused) "pause" else "play", size = 16.dp, color = TeddTokens.LimeStrong, strokeWidth = 2.2.dp)
                }
                Column(modifier = Modifier.weight(1f)) {
                    Text(
                        text = mmss(mins, secs),
                        color = TeddTokens.Ink, fontSize = 30.sp, fontWeight = FontWeight.Medium,
                        letterSpacing = (-1).sp, fontFamily = LocalTeddFonts.current.mono,
                        maxLines = 1, softWrap = false,
                    )
                    Text(label, color = TeddTokens.InkMuted, style = TeddType.Overline, maxLines = 1)
                }
            }
            // Les boutons ont leur ligne : à côté du temps, ils l'écrasaient.
            TimerButtons(model, hint = false, wide = true)
            if (!model.timerActive) TimerHint(model)
        }
    }
}

/** Où le temps de la prochaine course va tomber. Dit avant de lancer, pas après. */
@Composable
private fun TimerHint(model: FocusModel) {
    val skill = model.pendingSkillName()
    Text(
        text = skill?.let { "Ce temps comptera pour $it." } ?: "Ce temps ne comptera pour aucune compétence.",
        style = TeddType.Caption,
        color = TeddTokens.InkMuted,
    )
}

@Composable
private fun TimerButtons(model: FocusModel, hint: Boolean = true, wide: Boolean = false) {
    if (model.timerActive) {
        Row(horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
            TeddButton(
                text = if (model.timerPaused) "Reprendre" else "Pause",
                onClick = { model.togglePause() },
                kind = if (model.timerPaused) TeddButtonKind.Primary else TeddButtonKind.Secondary,
                icon = if (model.timerPaused) "play" else "pause",
            )
            TeddButton(text = "Terminer", onClick = { model.stopTimer() }, kind = TeddButtonKind.Ghost, icon = "check")
        }
    } else {
        Column(
            modifier = if (wide) Modifier.fillMaxWidth() else Modifier,
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(TeddSpace.S2),
        ) {
            TeddButton(
                text = "Démarrer la session",
                onClick = { model.startTimer() },
                kind = TeddButtonKind.Primary,
                size = TeddButtonSize.Lg,
                icon = "play",
                modifier = if (wide) Modifier.fillMaxWidth() else Modifier,
            )
            // Sans cette ligne, une course lancée à la volée ne compte pour rien et
            // personne ne s'en aperçoit avant de regarder ses compétences des semaines
            // plus tard. On dit à l'avance où le temps va tomber.
            if (hint) TimerHint(model)
        }
    }
}

/** Onglets Aujourd'hui / Historique, segmentés. */
@Composable
internal fun FocusTabs(tab: FocusTab, onChange: (FocusTab) -> Unit, modifier: Modifier = Modifier) {
    TeddTabs(
        tabs = listOf(
            TeddTab(FocusTab.Today.name, "Aujourd'hui"),
            TeddTab(FocusTab.History.name, "Historique"),
            TeddTab(FocusTab.Review.name, "Révisions"),
        ),
        selected = tab.name,
        onSelect = { name -> FocusTab.entries.firstOrNull { it.name == name }?.let(onChange) },
        modifier = modifier,
    )
}

/** Progression du jour : faites / total, barre, puis actions. */
@Composable
internal fun FocusProgressBlock(model: FocusModel, compact: Boolean, onExtend: (() -> Unit)?) {
    val h = if (compact) 16.dp else 24.dp
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(start = h, end = h, top = if (compact) 6.dp else 14.dp, bottom = if (compact) 6.dp else 10.dp),
    ) {
        Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.Bottom, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            Text("PROGRESSION", color = TeddTokens.InkMuted, fontSize = 11.sp, fontWeight = FontWeight.Bold, letterSpacing = 0.5.sp, modifier = Modifier.weight(1f))
            Text("${model.doneCount}/${model.totalCount}", color = TeddTokens.Ink, fontSize = 15.sp, fontWeight = FontWeight.SemiBold, fontFamily = LocalTeddFonts.current.mono)
            Text("tâches · ${model.progressPct}%", color = TeddTokens.InkMuted, fontSize = 12.sp)
        }
        // Le temps que la journée représente, à titre indicatif : ce qui est prévu, ce qui est déjà fait.
        val planned = model.plannedMinutes
        if (planned > 0) {
            Spacer(modifier = Modifier.height(2.dp))
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End, verticalAlignment = Alignment.CenterVertically) {
                Text(
                    text = "${formatMinutes(planned)} prévues · ${formatMinutes(model.doneMinutes)} faites · reste ${formatMinutes(planned - model.doneMinutes)}",
                    color = TeddTokens.InkMuted,
                    fontSize = 11.5.sp,
                    fontFamily = LocalTeddFonts.current.mono,
                )
            }
        }
        Spacer(modifier = Modifier.height(6.dp))
        Box(modifier = Modifier.fillMaxWidth().height(6.dp).clip(RoundedCornerShape(3.dp)).background(TeddTokens.SurfaceSunken, RoundedCornerShape(3.dp))) {
            Box(
                modifier = Modifier
                    .fillMaxWidth(model.progressPct.coerceIn(0, 100) / 100f)
                    .height(6.dp)
                    .clip(RoundedCornerShape(3.dp))
                    .background(TeddTokens.LimeStrong, RoundedCornerShape(3.dp)),
            )
        }
        val completed = model.currentSession?.status == "completed"
        if (model.canTerminate || completed || onExtend != null) {
            Spacer(modifier = Modifier.height(10.dp))
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                if (model.canTerminate) {
                    TeddButton(
                        text = "Terminer la journée", onClick = { model.terminateToday() },
                        kind = TeddButtonKind.Primary, size = TeddButtonSize.Sm,
                        leadingIcon = { TeddIcon(name = "check", size = 12.dp, color = TeddTokens.OnAction, strokeWidth = 2.4.dp) },
                    )
                }
                if (completed) {
                    Box(
                        modifier = Modifier.clip(RoundedCornerShape(TeddTokens.RFull)).background(TeddTokens.SuccessSoft, RoundedCornerShape(TeddTokens.RFull)).padding(horizontal = 10.dp, vertical = 4.dp),
                    ) { Text("Journée terminée", color = TeddTokens.Success, fontSize = 11.sp, fontWeight = FontWeight.SemiBold) }
                }
                if (onExtend != null) {
                    TeddButton(
                        text = if (model.hasExtension) "Modifier l'extension" else "Étendre",
                        onClick = onExtend, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Sm,
                        leadingIcon = { TeddIcon(name = "calendar", size = 12.dp, color = TeddTokens.InkMuted) },
                    )
                }
            }
        }
    }
}

/** Saisie rapide d'une tâche, validée par Entrée ou par le bouton. */
@Composable
internal fun FocusQuickAdd(model: FocusModel, compact: Boolean) {
    val h = if (compact) 16.dp else 24.dp
    Row(modifier = Modifier.fillMaxWidth().padding(start = h, end = h, top = if (compact) 6.dp else 12.dp, bottom = 6.dp)) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                .padding(horizontal = 12.dp, vertical = 8.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            TeddIcon(name = "plus", size = 14.dp, color = TeddTokens.LimeStrong, strokeWidth = 2.2.dp)
            BasicTextField(
                value = model.quickDraft,
                onValueChange = { model.quickDraft = it },
                modifier = Modifier.weight(1f),
                singleLine = true,
                textStyle = TextStyle(color = TeddTokens.Ink, fontSize = 13.sp),
                cursorBrush = SolidColor(TeddTokens.LimeStrong),
                keyboardOptions = KeyboardOptions(imeAction = ImeAction.Done),
                keyboardActions = KeyboardActions(onDone = { model.submitQuick() }),
                decorationBox = { inner ->
                    if (model.quickDraft.isEmpty()) Text("Ex : appeler le client, lire la doc…", color = TeddTokens.InkMuted, fontSize = 13.sp)
                    inner()
                },
            )
            // Le temps qu'on se donne, en minutes : facultatif, chiffres seulement.
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(3.dp),
                modifier = Modifier
                    .clip(RoundedCornerShape(TeddTokens.RXs))
                    .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RXs))
                    .padding(horizontal = 8.dp, vertical = 4.dp),
            ) {
                BasicTextField(
                    value = model.quickMinutesDraft,
                    onValueChange = { v -> model.quickMinutesDraft = v.filter { it.isDigit() }.take(3) },
                    modifier = Modifier.width(30.dp),
                    singleLine = true,
                    textStyle = TextStyle(color = TeddTokens.Ink, fontSize = 13.sp, fontFamily = LocalTeddFonts.current.mono, textAlign = TextAlign.End),
                    cursorBrush = SolidColor(TeddTokens.LimeStrong),
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number, imeAction = ImeAction.Done),
                    keyboardActions = KeyboardActions(onDone = { model.submitQuick() }),
                    decorationBox = { inner ->
                        Box(contentAlignment = Alignment.CenterEnd) {
                            if (model.quickMinutesDraft.isEmpty()) Text("—", color = TeddTokens.InkMuted, fontSize = 13.sp)
                            inner()
                        }
                    },
                )
                Text("min", color = TeddTokens.InkMuted, fontSize = 11.sp)
            }
            TeddButton(
                text = "Ajouter", onClick = { model.submitQuick() },
                kind = TeddButtonKind.Primary, size = TeddButtonSize.Sm,
                enabled = model.quickDraft.trim().isNotEmpty(),
            )
        }
    }
}

/**
 * Modification d'une tâche rapide : son titre et le temps qu'on se donne.
 * Même boîte en fenêtre compacte et large ; Entrée enregistre, Échap n'existe pas ici, on ferme au clic.
 */
@Composable
internal fun QuickEditDialog(item: Pomodoro_item, onClose: () -> Unit, onSave: (title: String, minutes: Long?) -> Unit) {
    var title by remember(item.id) { mutableStateOf(item.custom_title.orEmpty()) }
    var minutes by remember(item.id) { mutableStateOf(item.estimated_minutes?.toString().orEmpty()) }
    val submit = { onSave(title, minutes.toLongOrNull()) }
    Box(
        modifier = Modifier.fillMaxSize().background(Color.Black.copy(alpha = 0.42f))
            .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null, onClick = onClose),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier = Modifier
                .widthIn(max = 420.dp)
                .fillMaxWidth(0.92f)
                .clip(RoundedCornerShape(TeddTokens.RMd))
                .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RMd))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RMd))
                // Un clic dans la boîte ne la ferme pas.
                .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null, onClick = {})
                .padding(22.dp),
        ) {
            Text("Modifier la tâche rapide", color = TeddTokens.Ink, fontSize = 15.sp, fontWeight = FontWeight.SemiBold)
            Spacer(modifier = Modifier.height(14.dp))
            Text("TITRE", color = TeddTokens.InkMuted, fontSize = 11.sp, fontWeight = FontWeight.SemiBold, letterSpacing = 0.5.sp)
            Spacer(modifier = Modifier.height(6.dp))
            Row(
                modifier = Modifier.fillMaxWidth().height(38.dp).clip(RoundedCornerShape(TeddTokens.RSm))
                    .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm)).background(TeddTokens.Bg, RoundedCornerShape(TeddTokens.RSm))
                    .padding(horizontal = 12.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                BasicTextField(
                    value = title,
                    onValueChange = { title = it },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    textStyle = TextStyle(color = TeddTokens.Ink, fontSize = 13.sp),
                    cursorBrush = SolidColor(TeddTokens.LimeStrong),
                    keyboardOptions = KeyboardOptions(imeAction = ImeAction.Done),
                    keyboardActions = KeyboardActions(onDone = { submit() }),
                )
            }
            Spacer(modifier = Modifier.height(12.dp))
            Text("TEMPS PRÉVU", color = TeddTokens.InkMuted, fontSize = 11.sp, fontWeight = FontWeight.SemiBold, letterSpacing = 0.5.sp)
            Spacer(modifier = Modifier.height(6.dp))
            Row(
                modifier = Modifier.width(120.dp).height(38.dp).clip(RoundedCornerShape(TeddTokens.RSm))
                    .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm)).background(TeddTokens.Bg, RoundedCornerShape(TeddTokens.RSm))
                    .padding(horizontal = 12.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                BasicTextField(
                    value = minutes,
                    onValueChange = { v -> minutes = v.filter { it.isDigit() }.take(3) },
                    modifier = Modifier.weight(1f),
                    singleLine = true,
                    textStyle = TextStyle(color = TeddTokens.Ink, fontSize = 13.sp, fontFamily = LocalTeddFonts.current.mono, textAlign = TextAlign.End),
                    cursorBrush = SolidColor(TeddTokens.LimeStrong),
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number, imeAction = ImeAction.Done),
                    keyboardActions = KeyboardActions(onDone = { submit() }),
                    decorationBox = { inner ->
                        Box(contentAlignment = Alignment.CenterEnd) {
                            if (minutes.isEmpty()) Text("—", color = TeddTokens.InkMuted, fontSize = 13.sp)
                            inner()
                        }
                    },
                )
                Text("min", color = TeddTokens.InkMuted, fontSize = 12.sp)
            }
            Spacer(modifier = Modifier.height(16.dp))
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                Spacer(modifier = Modifier.weight(1f))
                TeddButton(text = "Annuler", onClick = onClose, kind = TeddButtonKind.Secondary, size = TeddButtonSize.Sm)
                TeddButton(text = "Enregistrer", onClick = submit, kind = TeddButtonKind.Primary, size = TeddButtonSize.Sm, enabled = title.trim().isNotEmpty())
            }
        }
    }
}

/** Bandeau discret quand la session est étendue au-delà du jour. */
@Composable
internal fun FocusExtensionBanner(startDate: String, endDate: String, onCancel: () -> Unit, compact: Boolean) {
    val h = if (compact) 16.dp else 24.dp
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(TeddTokens.LimeSoft)
            .padding(horizontal = h, vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        TeddIcon(name = "calendar", size = 12.dp, color = TeddTokens.LimeStrong, strokeWidth = 2.dp)
        Text("Session étendue", color = TeddTokens.LimeStrong, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
        Text("$startDate → $endDate", color = TeddTokens.InkMuted, fontSize = 11.sp, fontFamily = LocalTeddFonts.current.mono, modifier = Modifier.weight(1f))
        XCloseButton(size = 22, onClick = onCancel)
    }
}

/** Puces de durée : 5, 10, 25, 50 minutes selon les préréglages. */
@Composable
internal fun FocusDurationChips(current: Int, onPick: (Int) -> Unit, modifier: Modifier = Modifier) {
    Row(modifier = modifier, verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
        Text("DURÉE", color = TeddTokens.InkMuted, fontSize = 11.sp, fontWeight = FontWeight.SemiBold, letterSpacing = 0.5.sp)
        POM_PRESET_DURATIONS.forEach { m ->
            val on = current == m
            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(TeddTokens.RXs))
                    .background(if (on) TeddTokens.LimeSoft else Color.Transparent, RoundedCornerShape(TeddTokens.RXs))
                    .border(1.dp, if (on) TeddTokens.LimeStrong else TeddTokens.Line, RoundedCornerShape(TeddTokens.RXs))
                    .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null, onClick = { onPick(m) })
                    .padding(horizontal = 11.dp, vertical = 5.dp),
            ) {
                Text("$m min", color = if (on) TeddTokens.LimeStrong else TeddTokens.InkMuted, fontSize = 11.sp, fontWeight = FontWeight.SemiBold, fontFamily = LocalTeddFonts.current.mono)
            }
        }
    }
}

/** Message éphémère en bas de l'écran. */
@Composable
internal fun FocusSnackbar(message: String?, bottomPadding: Dp) {
    if (message == null) return
    Box(modifier = Modifier.fillMaxSize().padding(bottom = bottomPadding), contentAlignment = Alignment.BottomCenter) {
        Box(
            modifier = Modifier
                .clip(RoundedCornerShape(TeddTokens.RFull))
                .background(TeddTokens.Ink.copy(alpha = 0.92f), RoundedCornerShape(TeddTokens.RFull))
                .padding(horizontal = 18.dp, vertical = 10.dp),
        ) { Text(message, color = TeddTokens.Bg, fontSize = 12.5.sp, fontWeight = FontWeight.Medium) }
    }
}

/** État vide du jour, avec un seul geste proposé. */
@Composable
internal fun FocusEmptyToday(modifier: Modifier = Modifier) {
    Box(modifier = modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
        TeddEmptyState(
            title = "Aucune tâche pour aujourd'hui",
            message = "Écris une tâche ci-dessus, reprends une session passée, ou lance une micro-tâche depuis une compétence.",
            icon = "focus",
        )
    }
}

@Suppress("unused")
private val keepWidth = Modifier.width(0.dp)
